# Route: /tracking/records
# Search and browse tracking records with filters
# Permission: TrackingRecord:read

page-tracking-record-list:
  type: Page
  archetype: list
  meta:
    title: $t.tracking.records.meta_title
    route: /tracking/records
    layout: shell
    auth: required
  data:
    entries:
      model: TrackingRecord[]
      # F80 FR-8 — the browse default is EVERY entry the caller may see
      # (domain-scoped server-side per OQ-6); "Mine" is an explicit author
      # filter in the EntryFilterForm below, not the mount view. The
      # dashboard's "My Recent Entries" tile keeps the authorId=me intent for
      # operators returning to their own work. Valid TrackingRecordStatus
      # values: Draft, PendingReview, Complete, Amended
      # (see src/Xp87.Domain/Tracking/ValueObjects).
      refresh: on_mount
    # Request body for the Export-PDF action below — ExportPdfRequest
    # { template: "list" }. Exporting the CURRENT filter scope (mirroring
    # EntryFilterForm into the request) needs a dependent-field mechanism
    # the emitter doesn't have yet — F77 D-2 picks that up; until then the
    # export covers the unfiltered list.
    exportListRequest:
      model: ExportPdfForm
      initial:
        template: "list"
      data_states:
        loading:
          skeleton_count: 5
        empty:
          ref: record-list-empty
        error: default
    filters:
      model: EntryFilterForm
      initial:
        search: ""
        contextType: ""
        # The dropdowns default to "" (All) to match the unfiltered mount
        # view (UX M-03: the dropdown reflects the data on screen).
        status: ""
        from: ""
        to: ""
        authorId: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - record-list-headerbar
    - record-list-context-header
    - record-list-filters
    - record-list-table

# ── Header ───────────────────────────────────────────────────────────────────

# F80 FR-3 — Asset · Tracking · Shift chips. On the list the widget resolves
# the context from the URL (?contextId=&template=) — the tracking-landing
# links into the asset-scoped stream; the unfiltered list renders no chips.
record-list-context-header:
  type: Widget
  ref: xp87-tracking-context-header

# Header bar (title + actions on one row) — generic flex composition so the
# three action buttons sit ON the page title's line (justify space-between),
# not in a separate row below it. Reusable pattern for any page whose buttons
# are richer than the PageHeader `actions:` shorthand (here custom / download /
# data_source), so they stay full Button nodes composed onto the title line.
record-list-headerbar:
  type: Section
  spatial:
    display: flex
    direction: row
    justify: space-between
    align: center
    gap: $spacing.md
    wrap: wrap
  children:
    - record-list-header
    - record-list-actions

record-list-header:
  type: PageHeader
  content: $t.tracking.records.title

# The three action buttons keep their own node IDs because Tier 4/5 scenarios
# address them directly (`record-list-btn-create`, `record-list-btn-jump-to-latest`,
# `record-list-btn-export-pdf`). They render right-aligned on the title line via
# record-list-headerbar above.
#
# M1 rendering sweep (phone-width) — this row used to carry
# `responsive.sm.display: none`, hiding Create/Export/Jump-to-latest at phone
# width entirely. `wrap: wrap` lets the row reflow onto additional lines on
# narrow viewports instead of hiding; each button keeps its full touch-target
# size.
record-list-actions:
  type: Section
  spatial:
    display: flex
    direction: row
    justify: flex-end
    align: center
    gap: $spacing.sm
    wrap: wrap
  children:
    - record-list-btn-jump-to-latest
    - record-list-btn-export-pdf
    - record-list-btn-create

record-list-btn-create:
  type: Button
  variant: filled
  color: info
  content: $t.tracking.records.btn_new
  a11y:
    label: $t.tracking.records.btn_new
  action:
    type: navigate
    route: /tracking/records/new
    # F79 — Cancel / on_success on the create screen returns to this list.
    return_context: origin_only
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

# F53b E.2 — "Go to latest" jump button. Anchors the user to the top row
# of the table (newest first by ORDER BY timestamp DESC) without changing
# pagination state.
record-list-btn-jump-to-latest:
  type: Button
  variant: outlined
  color: info
  content: $t.tracking.records.btn_jump_to_latest
  a11y:
    label: $t.tracking.records.btn_jump_to_latest_label
  action:
    type: custom
    data_source: entries
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

# F53b E.6 — "Export PDF" action calls the QuestPDF-backed endpoint that A9
# ships under `POST /api/tracking/export-pdf`; the browser downloads the
# application/pdf stream via the action's `download: true` blob path.
record-list-btn-export-pdf:
  type: Button
  variant: outlined
  content: $t.tracking.records.btn_export_pdf
  icon: $icon.download-simple
  a11y:
    label: $t.tracking.records.btn_export_pdf_label
  action:
    type: custom
    method: POST
    url: /api/tracking/export-pdf
    body: exportListRequest
    download: true
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

# ── Filter bar ───────────────────────────────────────────────────────────────

record-list-filters:
  type: Section
  spatial:
    display: flex
    gap: $spacing.md
    padding: $spacing.md
    direction: row
    align: flex-end
    wrap: wrap
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - record-filter-search
    - record-filter-context
    - record-filter-status
    - record-filter-mine
    - record-filter-from
    - record-filter-to
    - record-filter-apply
    - record-filter-clear

record-filter-search:
  type: Input
  input_type: text
  # F53b E.1 — wildcard hint surfaced via placeholder: '*foo*bar*'.
  label: $t.tracking.records.filter_search
  placeholder: $t.tracking.records.filter_search_placeholder
  bind: filters.search
  a11y:
    label: $t.tracking.records.filter_search_label
  spatial:
    flex: 2
    min_width: 12rem

# The three selects name EntryFilterForm themselves: they sit in a Section, not
# a Form, so no ancestor supplies the model, and without it the emitter cannot
# resolve the bound field's enum values and renders a select with no options.
record-filter-context:
  type: Input
  input_type: select
  model: EntryFilterForm
  label: $t.tracking.records.filter_context
  bind: filters.contextType
  a11y:
    label: $t.tracking.records.filter_context_label
  spatial:
    flex: 1
    min_width: 8rem

record-filter-status:
  type: Input
  input_type: select
  model: EntryFilterForm
  label: $t.tracking.records.filter_status
  bind: filters.status
  a11y:
    label: $t.tracking.records.filter_status_label
  spatial:
    flex: 1
    min_width: 8rem

record-filter-mine:
  type: Input
  input_type: select
  model: EntryFilterForm
  label: $t.tracking.records.filter_author
  bind: filters.authorId
  a11y:
    label: $t.tracking.records.filter_author_label
  spatial:
    flex: 1
    min_width: 8rem

record-filter-from:
  type: Input
  input_type: datetime
  label: $t.tracking.records.filter_from
  bind: filters.from
  a11y:
    label: $t.tracking.records.filter_from_label
  spatial:
    flex: 1
    min_width: 10rem

record-filter-to:
  type: Input
  input_type: datetime
  label: $t.tracking.records.filter_to
  bind: filters.to
  a11y:
    label: $t.tracking.records.filter_to_label
  spatial:
    flex: 1
    min_width: 10rem

record-filter-apply:
  type: Button
  content: $t.tracking.records.btn_apply
  variant: outlined
  color: info
  a11y:
    label: $t.tracking.records.btn_apply_label
  action:
    type: custom
    data_source: entries
    params: filters
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

record-filter-clear:
  type: Button
  variant: ghost
  content: $t.common.filter_clear
  a11y:
    label: $t.common.filter_clear_label
  action:
    type: custom
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md

# ── Results table ────────────────────────────────────────────────────────────

record-list-table:
  type: Table
  bind:
    rows: entries
  a11y:
    label: $t.tracking.records.table_label
  # XP87 #1400 — the list is one page of the server query; the pager states
  # "Showing X–Y of N" from the API's `total` and pages that query, so a result
  # longer than one page never reads as the whole result.
  pagination:
    mode: pages
  # No client-side quick-filter here: record-list already has a server-side search
  # box in its filter bar above (`record-filter-search` → `filters.search`), which
  # is the correct tool for a server-paginated list. A second client quick-filter
  # would put two search boxes on one screen and only narrow the loaded page,
  # reading as a full search while silently missing unloaded rows. The full fix —
  # making the server `search=` matching case-insensitive substring/full-text over
  # the whole result set (finding 4.6) — is the M2 server-side item
  # (XP87 issue #596 §1). DEC-090 quick-filter is adopted on
  # the bounded list screens that had no search of their own.
  spatial:
    overflow: auto
  columns:
    # Humanized short timestamp (today "14:32" / this year "Jun 12, 14:32" /
    # older "2025-11-03 medium date"); the full compliance-datetime stays
    # reachable in the cell's title tooltip (regulated MES — precision matters).
    - key: timestamp
      label: $t.tracking.records.col_timestamp
      format: datetime-short
      # Single-line with ellipsis: an older-date short form ("Nov 3, 2025, 14:32")
      # can run a few px past this compact column; the full compliance-datetime
      # stays in the cell's title tooltip. (Also satisfies the 354 no-truncation
      # guard, which exempts the deliberate ellipsis+overflow-hidden recipe.)
      truncate: true
      width: 1fr
    # Single-line text preview with CSS ellipsis; full text in title tooltip.
    # Holds 1.7fr so the two badge columns below (next signer, status) have room:
    # the table is table-layout:fixed with colgroup % widths, and a non-wrapping
    # badge wider than its column overflows the row (the 354 desktop-overflow
    # guard). The narrative truncates cleanly and keeps the full value in its
    # title tooltip.
    - key: text
      label: $t.tracking.records.col_text
      truncate: true
      width: 1.7fr
    # Resolved author display name (F75) runs long for full names / emails —
    # single-line ellipsis, full value in the title tooltip (same treatment as
    # the template + context columns; also satisfies the 354 no-truncation guard).
    - key: authorDisplayName
      priority: medium
      label: $t.tracking.records.col_author
      truncate: true
      width: 1fr
    # F80 — logical template names and resolved asset display names run long;
    # single-line ellipsis with the full value in the title tooltip (same
    # treatment as the text column).
    - key: formName
      priority: medium
      label: $t.tracking.records.col_form
      truncate: true
      width: 1fr
    - key: contextDisplayName
      label: $t.tracking.records.col_context
      truncate: true
      width: 1fr
    # FR-2 — the associated entity's TYPE as a badge (Asset / Area / General).
    # A General entry has a blank display name, so this badge is what makes the
    # row honest rather than an empty context cell. Binds the server-localized
    # ContextTypeLabel (Asset / Area / General resolved via
    # enums.TrackingContextType.*) — NOT the raw ContextType — so the badge reads
    # correctly in every locale. Category, not state: all three types share the
    # neutral info tone (colour encodes state, never mere category).
    - key: contextTypeLabel
      priority: low
      label: $t.tracking.records.col_context_type
      width: 0.8fr
      display: badge
      variant: info
    # FR-3c — next required signer (server-computed NextSignerLabel, localized):
    # "Complete", "Awaiting you", or "Awaiting <role>". The read model supplies the
    # whole string (the DataTable cell cannot express conditional text). Rendered as
    # a badge whose tone is ALSO server-driven (NextSignerTone): caution (attention
    # needed) on the caller's own actionable rows, else the neutral info tone — so an
    # operator can visually scan a busy list for the rows waiting on THEM. The tone
    # is bound directly from the row (not a client-side boolean check on
    # awaitingMySignature — the badge's conditional variant only string-compares).
    - key: nextSignerLabel
      priority: low
      label: $t.tracking.records.col_next_signer
      truncate: true
      width: 1.6fr
      display: badge
      variant: $row.nextSignerTone
    # (No version column — FR-4 is explicitly "No UI change": latest-version-only
    # browse is a query concern (is_amended=false default), and the status badge
    # already reads "Amended" for a superseded row. A separate version-number
    # column was an unspecified 9th column that pushed the table past the desktop
    # width — the detail header's "v{n} / {m}" badge is the amendment-position UI.)
    - key: status
      label: $t.tracking.records.col_status
      # The cell shows TrackingRecord.status's value_labels (models.cir.yaml), never
      # the raw enum. The widest label is the French "En attente de revue" (19
      # characters, about 10rem as a 600-weight badge). 2fr of 10.1fr is about
      # 11rem of cell at a 1280px viewport beside the sidebar, which holds it —
      # an estimate from the column arithmetic, not a browser measurement.
      # Narrower than that, `truncate` clips the badge inside its own cell and the
      # title tooltip carries the full label, so the badge never bleeds past the
      # table edge.
      width: 2fr
      truncate: true
      display: badge
      variant:
        when: "$row.status == 'Complete'"
        value: go
        else:
          when: "$row.status == 'PendingReview'"
          value: caution
          else:
            when: "$row.status == 'Amended'"
            value: stop
            else: info
  row_action:
    type: navigate
    route: /tracking/records/:id
    params:
      id: $row.id
    # F79 — opening a row carries the origin so the detail's Back returns to this
    # list (origin_only this pass; list_state restoration is A-phase-2).
    return_context: origin_only
  empty: record-list-empty

record-list-empty:
  type: EmptyState
  content: $t.tracking.records.empty
  body: $t.tracking.records.empty_body
